/**
 * The "+" menu of a tab strip: the pages this user may open (the sidebar's,
 * grouped like the sidebar, then every other page the command palette can
 * open), with a filter box once the list is long. Shared by the tabbed pop-out
 * window and the main window's strip.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

export interface PageOption {
  tab: string;
  label: string;
  group?: string;
  icon?: React.ElementType;
  /** Extra search terms (the command palette's aliases for the page). */
  keywords?: string;
}

/** Above this many pages the menu shows a filter box. Every match is listed — there is no cap. */
const FILTER_THRESHOLD = 12;

/**
 * Pages matching a filter query, grouped (groups in first-seen order). Every
 * word of the query must appear in the page's label, its palette keywords, its
 * group or its id — the same things the command palette searches.
 */
export function groupPageOptions(pages: PageOption[], query: string): Array<{ group: string; items: PageOption[] }> {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const out: Array<{ group: string; items: PageOption[] }> = [];
  const byGroup = new Map<string, { group: string; items: PageOption[] }>();
  for (const p of pages) {
    if (words.length) {
      const hay = `${p.label} ${p.keywords || ''} ${p.group || ''} ${p.tab}`.toLowerCase();
      if (!words.every((w) => hay.includes(w))) continue;
    }
    const g = p.group || '';
    let bucket = byGroup.get(g);
    if (!bucket) {
      bucket = { group: g, items: [] };
      byGroup.set(g, bucket);
      out.push(bucket);
    }
    bucket.items.push(p);
  }
  return out;
}

interface PagePickerItemsProps {
  pages: PageOption[];
  onPick: (page: PageOption) => void;
  /** Pages already open somewhere in this strip (marked "open"). */
  openKeys?: Set<string>;
  /** data-testid prefix for each item: `${prefix}${tab}`. */
  testIdPrefix?: string;
}

/** The menu's items — render inside a DropdownMenuContent. */
export function PagePickerItems({ pages, onPick, openKeys, testIdPrefix = 'popout-add-' }: PagePickerItemsProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const groups = useMemo(() => groupPageOptions(pages, query), [pages, query]);
  const filterRef = useRef<HTMLDivElement>(null);
  const showFilter = pages.length > FILTER_THRESHOLD;
  // A long list is found by typing: put the cursor in the box once the menu has
  // opened (Radix focuses the menu itself on open, so this waits a frame).
  useEffect(() => {
    if (!showFilter) return;
    const id = requestAnimationFrame(() => filterRef.current?.querySelector('input')?.focus());
    return () => cancelAnimationFrame(id);
  }, [showFilter]);

  if (!pages.length) {
    return (
      <div className="px-2 py-1.5 text-sm text-muted-foreground">{t('appPopout.no_pages', 'No pages available.')}</div>
    );
  }

  return (
    <>
      {showFilter && (
        <div ref={filterRef} className="px-1.5 pb-1 pt-0.5">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              // Radix menus move focus on letter keys (typeahead); keep the keys in the box.
              e.stopPropagation();
              if (e.key !== 'ArrowDown' && e.key !== 'Enter') return;
              const first = filterRef.current
                ?.closest('[role="menu"]')
                ?.querySelector<HTMLElement>('[role="menuitem"][data-page-option]');
              if (!first) return;
              e.preventDefault();
              // ArrowDown: into the results — from there the menu's own arrow keys
              // walk every group. Enter: open the best (first) match, as a click
              // would (which also closes the menu).
              if (e.key === 'ArrowDown') first.focus();
              else first.click();
            }}
            placeholder={t('mainTabs.filter_pages', 'Find a page…')}
            aria-label={t('mainTabs.filter_pages', 'Find a page…')}
            className="w-full rounded-md border border-slate-200 bg-transparent px-2 py-1 text-sm outline-none focus:border-slate-400 dark:border-slate-700"
            data-testid="page-picker-filter"
          />
        </div>
      )}
      {!groups.length && (
        <div className="px-2 py-1.5 text-sm text-muted-foreground">{t('mainTabs.no_match', 'No matching pages.')}</div>
      )}
      {groups.map((g, gi) => (
        <React.Fragment key={g.group || `g${gi}`}>
          {gi > 0 && <DropdownMenuSeparator />}
          {g.group && <DropdownMenuLabel className="text-xs text-muted-foreground">{g.group}</DropdownMenuLabel>}
          {g.items.map((p) => (
            <DropdownMenuItem
              key={p.tab}
              onSelect={() => onPick(p)}
              data-testid={`${testIdPrefix}${p.tab}`}
              data-page-option=""
              className="gap-2"
            >
              {p.icon && <p.icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden />}
              <span className="truncate">{p.label}</span>
              {openKeys?.has(p.tab) && (
                <span className="ml-auto pl-2 text-xs text-muted-foreground">{t('appPopout.already_open', 'open')}</span>
              )}
            </DropdownMenuItem>
          ))}
        </React.Fragment>
      ))}
    </>
  );
}

export default PagePickerItems;
